/**
 * ====================================
 * KLASYS - Licence de l'ecole (cote client)
 * ====================================
 *
 * Expose l'instantane de licence diffuse par le CIK, pour masquer les entrees
 * de menu et les ecrans des modules non souscrits.
 *
 * ⚠️ Ce masquage est un confort d'affichage, jamais une securite. La securite
 * est le middleware `requireFeature` cote serveur (backend2/middlewares/
 * features.js). Un utilisateur qui forcerait une URL masquee ici recevrait un
 * 403 du serveur : c'est cela qui protege, pas ce fichier.
 *
 * A ne pas confondre avec `usePermissions` :
 *   - la licence dit ce que l'ECOLE a achete       → ce contexte
 *   - la permission dit ce que l'EMPLOYE peut faire → usePermissions
 * Les deux se composent : il faut les deux pour voir une entree de menu.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const FeaturesContext = createContext(null);

/**
 * Etat par defaut : aucune restriction connue.
 *
 * C'est volontairement l'etat le plus ouvert. Tant que la reponse du serveur
 * n'est pas arrivee, on n'a aucune raison de cacher quoi que ce soit — et
 * cacher puis reafficher produirait un clignotement du menu a chaque
 * chargement de page.
 */
const ETAT_OUVERT = {
    restreint: false,
    fonctionnalites: null,
    libelles: null,
    licence: null,
    diffuseeLe: null
};

// Cache local de l'instantané.
//
// Sans lui, chaque chargement de page attend le réseau avant de pouvoir dire
// quoi afficher — et le menu reste sur son écran d'attente. Avec lui, le menu
// se peuple immédiatement dans son état connu, et la réponse du serveur vient
// le corriger si la licence a bougé.
//
// localStorage est cloisonné par origine, donc `csap1.localhost` et
// `ecole-test.localhost` ne partagent rien. Le champ `ecole` est vérifié malgré
// tout : deux écoles derrière un même nom d'hôte ne doivent jamais se mélanger.
const CLE_CACHE = 'klasysLicence';

const lireCache = () => {
    try {
        const brut = localStorage.getItem(CLE_CACHE);
        if (!brut) return null;
        const cache = JSON.parse(brut);
        if (!cache || typeof cache.restreint !== 'boolean') return null;
        return cache;
    } catch (e) {
        return null;
    }
};

const ecrireCache = (reponse) => {
    try {
        localStorage.setItem(CLE_CACHE, JSON.stringify(reponse));
    } catch (e) {
        /* navigation privée ou stockage plein : on s'en passe */
    }
};

const viderCache = () => {
    try {
        localStorage.removeItem(CLE_CACHE);
    } catch (e) {
        /* sans effet */
    }
};

const versEtat = (reponse) => ({
    restreint: reponse.restreint,
    fonctionnalites: Array.isArray(reponse.fonctionnalites)
        ? new Set(reponse.fonctionnalites)
        : null,
    libelles: reponse.libelles || null,
    licence: reponse.licence || null,
    diffuseeLe: reponse.diffusee_le || null
});

export const useFeatures = () => {
    const contexte = useContext(FeaturesContext);
    if (!contexte) {
        throw new Error("useFeatures doit etre utilise a l'interieur d'un FeaturesProvider");
    }
    return contexte;
};

/**
 * Raccourci pour un seul test : `const cantineOuverte = useFeature('module.cantine')`
 */
export const useFeature = (cle) => {
    const { estActive } = useFeatures();
    return estActive(cle);
};

export const FeaturesProvider = ({ children }) => {
    // Le cache est lu à la construction, pas dans un effet : le premier rendu
    // dispose déjà de l'état connu, et le menu n'a rien à masquer puis remontrer.
    const cacheInitial = localStorage.getItem('token') ? lireCache() : null;

    const [etat, setEtat] = useState(cacheInitial ? versEtat(cacheInitial) : ETAT_OUVERT);
    const [chargement, setChargement] = useState(!cacheInitial);
    const [erreur, setErreur] = useState(null);

    const charger = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            // Pas connecté : rien à filtrer, et l'appel échouerait de toute façon.
            viderCache();
            setEtat(ETAT_OUVERT);
            setChargement(false);
            return;
        }

        try {
            const reponse = await api.licence.getMonInstantane();

            // `handleResponse` renvoie le corps tel quel en cas de succès, et un
            // objet `{ success: false }` en cas d'erreur réseau ou métier. La
            // présence de `restreint` distingue les deux sans ambiguïté.
            if (!reponse || typeof reponse.restreint !== 'boolean') {
                throw new Error(reponse?.error || 'Réponse de licence inattendue');
            }

            setEtat(versEtat(reponse));
            ecrireCache(reponse);
            setErreur(null);
        } catch (e) {
            // Une licence illisible ne doit pas amputer l'application. Si un
            // instantané est déjà en cache on le garde — il vaut mieux que rien —
            // sinon on ouvre tout et on laisse le serveur trancher.
            console.warn('[LICENCE] Instantané indisponible :', e.message);
            if (!lireCache()) setEtat(ETAT_OUVERT);
            setErreur(e.message);
        } finally {
            setChargement(false);
        }
    }, []);

    useEffect(() => {
        charger();
    }, [charger]);

    // Un changement de licence fait dans le CIK doit atteindre une session
    // déjà ouverte sans reconnexion : relecture au retour sur l'onglet (au
    // plus une fois par minute) et toutes les cinq minutes. Le serveur relit
    // lui-même la licence toutes les 30 secondes (tenant-manager.js). La
    // relecture est silencieuse : le menu ne passe jamais par un état vide.
    useEffect(() => {
        let derniereLecture = Date.now();
        const relire = () => {
            if (!localStorage.getItem('token')) return;
            derniereLecture = Date.now();
            charger();
        };
        const surVisibilite = () => {
            if (document.visibilityState === 'visible' && Date.now() - derniereLecture > 60 * 1000) relire();
        };
        const minuterie = setInterval(relire, 5 * 60 * 1000);
        document.addEventListener('visibilitychange', surVisibilite);
        return () => {
            clearInterval(minuterie);
            document.removeEventListener('visibilitychange', surVisibilite);
        };
    }, [charger]);

    // Rechargement a la connexion / deconnexion, comme le fait usePermissions.
    useEffect(() => {
        const surChangement = (e) => {
            if (e.key !== 'token') return;
            if (!e.newValue) {
                viderCache();
                setEtat(ETAT_OUVERT);
                setChargement(false);
            } else if (e.oldValue !== e.newValue) {
                viderCache();
                setChargement(true);
                charger();
            }
        };
        window.addEventListener('storage', surChangement);
        return () => window.removeEventListener('storage', surChangement);
    }, [charger]);

    /**
     * Cette fonctionnalite est-elle ouverte pour cette ecole ?
     *
     * Renvoie `true` tant qu'aucune restriction n'est connue — pendant le
     * chargement, apres une erreur, ou pour une ecole que le CIK n'a pas encore
     * diffusee. Meme politique que le serveur, pour que les deux ne divergent
     * jamais.
     */
    const estActive = useCallback((cle) => {
        if (!etat.restreint || !etat.fonctionnalites) return true;
        return etat.fonctionnalites.has(cle);
    }, [etat]);

    /** Vrai si au moins une des cles est ouverte. */
    const auMoinsUne = useCallback((cles) => {
        if (!Array.isArray(cles) || cles.length === 0) return true;
        return cles.some((c) => estActive(c));
    }, [estActive]);

    const valeur = {
        chargement,
        erreur,
        restreint: etat.restreint,
        licence: etat.licence,
        diffuseeLe: etat.diffuseeLe,
        fonctionnalites: etat.fonctionnalites ? [...etat.fonctionnalites] : null,
        libelle: (cle) => etat.libelles?.[cle] || cle,
        estActive,
        auMoinsUne,
        recharger: charger
    };

    return (
        <FeaturesContext.Provider value={valeur}>
            {children}
        </FeaturesContext.Provider>
    );
};

export default FeaturesContext;
